"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect, useCallback } from "react";
import { useTracking } from "react-tracking";
import * as Icons from "@nxt-costco-com/forge-icons";
import { MIN_HEIGHT_SIX_LINES_PX, TEXT_TYPES, COMPONENT_NAME, VISIBLE_LINES_CLAMP, TITLE, TITLE_WITH_XL_V2, HEADING_V2, TITLE_V2, SUBTITLE, SUBTITLE_V2, SUBHEADING_V2, BODY, BODY_V2, BODY_COPY_V2, SMALL_BODY_V2, DISCLAIMER, DISCLAIMER_V2, CAPTION_V2, HIDE_BUTTON_ID, SHOW_BUTTON_ID, TITLE_ID } from "./constants.js";
import { getTextBuilderTextId } from "../lib.js";
import { HeaderImage, IconImgBox, MainContainer, ContentContainer, Content, BodyText, Title, Subtitle, Body, Disclaimer, ShowHideButton, AccordionTitle, StyledChevronUp, StyledChevronDown } from "./styles.js";
const TextBuilderCard = ({
  alignment = "left",
  ariaLabel,
  colorConfig,
  iconImage,
  iconAltText,
  isEnableSeeMoreSeeLess = false,
  isIconAsImage = false,
  numberOfVisibleLines,
  textSections,
  sectionType = "full",
  seeMoreText,
  seeLessText,
  externalLink,
  uniqueId,
  locale,
  predefinedIcon,
  imageSize
}) => {
  const { trackEvent } = useTracking();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEllipsis, setIsEllipsis] = useState(false);
  const bodyTextRef = useRef(null);
  const hasCheckedEllipsis = useRef(false);
  const isDisplayImage = isIconAsImage && alignment === "center";
  const {
    text_color: { hex: textColor = "" } = {},
    background_gradient_style: backgroundGradientStyle = "no-background",
    background_gradient_color: {
      color1: { hex: firstBackgroundColor = "" } = {},
      color2: { hex: secondBackgroundColor = "" } = {}
    } = {},
    background_color: { hex: backgroundColor = "" } = {},
    border_color: { solid: { hex: borderColor = "" } = {} } = {}
  } = colorConfig ?? {};
  useEffect(() => {
    var _a;
    if (hasCheckedEllipsis.current) return;
    const bodyHeight = (_a = bodyTextRef.current) == null ? void 0 : _a.clientHeight;
    if (sectionType === "full" && (numberOfVisibleLines || isEnableSeeMoreSeeLess && bodyHeight && bodyHeight > MIN_HEIGHT_SIX_LINES_PX)) {
      setIsEllipsis(true);
      hasCheckedEllipsis.current = true;
    }
  }, [isEnableSeeMoreSeeLess, numberOfVisibleLines, sectionType]);
  const sections = textSections == null ? void 0 : textSections.filter(
    (section) => {
      var _a;
      return TEXT_TYPES.includes((_a = section.select_text_type) == null ? void 0 : _a.toString());
    },
    []
  );
  const toggleShowHide = useCallback(() => {
    setIsExpanded((prev) => {
      var _a;
      if (!prev) (_a = bodyTextRef.current) == null ? void 0 : _a.focus();
      return !prev;
    });
  }, []);
  const isCustomParsingEnabled = textSections == null ? void 0 : textSections.map(
    (item) => item.enable_custom_tag_parsing
  );
  const onContentClick = useCallback(
    (event) => {
      var _a;
      if (event.target instanceof Element && event.target.localName === "a") {
        trackEvent({
          trackType: "ad",
          itemCuration: "costco",
          adItem: {
            type: "ad",
            style: "text builder card",
            title: (_a = event.target) == null ? void 0 : _a.textContent
          }
        });
      }
    },
    [trackEvent]
  );
  const headerImage = isDisplayImage && /* @__PURE__ */ jsx(HeaderImage, { alt: iconAltText, src: iconImage == null ? void 0 : iconImage.url });
  const ForgeIcon = predefinedIcon ? Icons[predefinedIcon] : null;
  const iconBox = !isDisplayImage ? (iconImage == null ? void 0 : iconImage.url) ? /* @__PURE__ */ jsx(
    IconImgBox,
    {
      alignment,
      alt: iconAltText,
      loading: "lazy",
      src: iconImage == null ? void 0 : iconImage.url,
      imageSize
    }
  ) : ForgeIcon ? /* @__PURE__ */ jsx(ForgeIcon, { sx: { width: "20px", height: "20px" } }) : null : null;
  const bodyTextLines = isEllipsis ? isEnableSeeMoreSeeLess ? VISIBLE_LINES_CLAMP : numberOfVisibleLines : null;
  const bodyTextClassName = isEllipsis && !isExpanded ? "body-text-ellipsis" : "";
  const onContainerClick = (externalLink == null ? void 0 : externalLink.href) ? () => {
    var _a;
    if (window) {
      (_a = window.open(externalLink.href, "_blank")) == null ? void 0 : _a.focus();
    }
  } : void 0;
  const cardId = uniqueId ? `${uniqueId}_${COMPONENT_NAME}` : `${COMPONENT_NAME}`;
  return /* @__PURE__ */ jsxs(
    MainContainer,
    {
      alignment,
      backgroundColor,
      backgroundGradientStyle,
      "data-testid": cardId,
      firstBackgroundColor,
      isIconAsImage: isDisplayImage,
      secondBackgroundColor,
      sectionType,
      textColor,
      onClick: onContainerClick,
      "aria-label": ariaLabel,
      hoverAsClickable: !!externalLink,
      borderColor,
      children: [
        headerImage,
        /* @__PURE__ */ jsxs(
          ContentContainer,
          {
            alignment,
            backgroundGradientStyle,
            "data-testid": `${COMPONENT_NAME}-content`,
            isIconAsImage: isDisplayImage,
            sectionType,
            textColor,
            isForgeIcon: !!predefinedIcon,
            isCustomParsingEnabled: isCustomParsingEnabled == null ? void 0 : isCustomParsingEnabled.includes(true),
            children: [
              iconBox,
              /* @__PURE__ */ jsxs(Content, { alignment, children: [
                /* @__PURE__ */ jsx(
                  BodyText,
                  {
                    className: bodyTextClassName,
                    lines: bodyTextLines,
                    "data-testid": `${COMPONENT_NAME}-bodytext`,
                    onClick: (e) => {
                      e.stopPropagation();
                      onContentClick(e);
                    },
                    ref: bodyTextRef,
                    ...isEnableSeeMoreSeeLess && { tabIndex: -1 },
                    children: sections == null ? void 0 : sections.map((section, index) => {
                      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y;
                      if (((_a = section.select_text_type) == null ? void 0 : _a.toString()) === TITLE || ((_b = section.select_text_type) == null ? void 0 : _b.toString()) === TITLE_WITH_XL_V2 || ((_c = section.select_text_type) == null ? void 0 : _c.toString()) === HEADING_V2 || ((_d = section.select_text_type) == null ? void 0 : _d.toString()) === TITLE_WITH_XL_V2 || ((_e = section.select_text_type) == null ? void 0 : _e.toString()) === TITLE_V2) {
                        return /* @__PURE__ */ jsx(
                          Title,
                          {
                            id: getTextBuilderTextId({
                              uniqueId,
                              type: section.select_text_type || "",
                              text: section.markdown_text || ""
                            }),
                            alignment: section.text_alignment || alignment,
                            className: "textbuildercard-ui-cardTitle",
                            color: ((_g = (_f = section.color) == null ? void 0 : _f.text_color) == null ? void 0 : _g.hex) || textColor,
                            component: section.select_semantics_type,
                            "data-testid": `${COMPONENT_NAME}-title`,
                            isMarkdown: true,
                            variant: section == null ? void 0 : section.select_text_type,
                            locale,
                            isCustomParsingEnabled: section.enable_custom_tag_parsing,
                            children: section.markdown_text
                          },
                          `${section.markdown_text}-${index}`
                        );
                      } else if (((_h = section.select_text_type) == null ? void 0 : _h.toString()) === SUBTITLE || ((_i = section.select_text_type) == null ? void 0 : _i.toString()) === SUBTITLE_V2 || ((_j = section.select_text_type) == null ? void 0 : _j.toString()) === SUBHEADING_V2) {
                        return /* @__PURE__ */ jsx(
                          Subtitle,
                          {
                            id: getTextBuilderTextId({
                              uniqueId,
                              type: section.select_text_type || "",
                              text: section.markdown_text || ""
                            }),
                            alignment: section.text_alignment || alignment,
                            isMarkdown: true,
                            className: "textbuildercard-ui-cardSubtitle",
                            color: ((_l = (_k = section.color) == null ? void 0 : _k.text_color) == null ? void 0 : _l.hex) || textColor,
                            component: section.select_semantics_type,
                            variant: section == null ? void 0 : section.select_text_type,
                            locale,
                            isCustomParsingEnabled: section.enable_custom_tag_parsing,
                            children: section.markdown_text
                          },
                          `${section.markdown_text}-${index}`
                        );
                      } else if (((_m = section.select_text_type) == null ? void 0 : _m.toString()) === BODY || ((_n = section.select_text_type) == null ? void 0 : _n.toString()) === BODY_V2 || ((_o = section.select_text_type) == null ? void 0 : _o.toString()) === BODY_COPY_V2 || ((_p = section.select_text_type) == null ? void 0 : _p.toString()) === SMALL_BODY_V2)
                        return /* @__PURE__ */ jsx(
                          Body,
                          {
                            alignment: section.text_alignment || alignment,
                            color: ((_r = (_q = section.color) == null ? void 0 : _q.text_color) == null ? void 0 : _r.hex) || textColor,
                            customLinkColor: ((_t = (_s = section == null ? void 0 : section.color) == null ? void 0 : _s.link_color) == null ? void 0 : _t.hex) || "",
                            component: "div",
                            isMarkdown: true,
                            variant: section == null ? void 0 : section.select_text_type,
                            locale,
                            isCustomParsingEnabled: section.enable_custom_tag_parsing,
                            children: section.markdown_text
                          },
                          `${section.markdown_text}-${index}`
                        );
                      else if (((_u = section.select_text_type) == null ? void 0 : _u.toString()) === DISCLAIMER || ((_v = section.select_text_type) == null ? void 0 : _v.toString()) === DISCLAIMER_V2 || ((_w = section.select_text_type) == null ? void 0 : _w.toString()) === CAPTION_V2) {
                        return /* @__PURE__ */ jsx(
                          Disclaimer,
                          {
                            alignment: section.text_alignment || alignment,
                            className: `textbuildercard-ui-disclaimer ${isEllipsis && !isExpanded ? "textbuildercard-ui-disclaimer-hidden" : ""}`,
                            color: ((_y = (_x = section.color) == null ? void 0 : _x.text_color) == null ? void 0 : _y.hex) || textColor,
                            component: "div",
                            isMarkdown: true,
                            variant: section == null ? void 0 : section.select_text_type,
                            locale,
                            isCustomParsingEnabled: section.enable_custom_tag_parsing,
                            children: section.markdown_text
                          },
                          `${section.markdown_text}-${index}`
                        );
                      } else {
                        return section.select_text_type;
                      }
                    })
                  }
                ),
                isEllipsis && /* @__PURE__ */ jsxs(
                  ShowHideButton,
                  {
                    "aria-labelledby": `${isExpanded ? HIDE_BUTTON_ID : SHOW_BUTTON_ID} ${TITLE_ID}`,
                    onKeyDown: (event) => {
                      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                        toggleShowHide();
                      }
                    },
                    alignment,
                    "aria-expanded": isExpanded,
                    "data-testid": `${COMPONENT_NAME}-showhidebutton`,
                    id: isExpanded ? HIDE_BUTTON_ID : SHOW_BUTTON_ID,
                    onClick: toggleShowHide,
                    variant: "tertiary",
                    children: [
                      /* @__PURE__ */ jsx(AccordionTitle, { color: textColor, children: isExpanded ? seeLessText : seeMoreText }),
                      isExpanded ? /* @__PURE__ */ jsx(StyledChevronUp, { color: textColor }) : /* @__PURE__ */ jsx(StyledChevronDown, { color: textColor })
                    ]
                  }
                )
              ] })
            ]
          }
        )
      ]
    }
  );
};
TextBuilderCard.displayName = COMPONENT_NAME;
export {
  TextBuilderCard
};
